import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { DollarSign, Download, TrendingUp, CreditCard, Loader2, Camera, Trash2 } from 'lucide-react';
import { apiPayments, apiInvoices, apiCustomers } from '../../lib/api';
import { toCSV, downloadFile } from '../../lib/importExport';
import { toast } from 'sonner';
import BulkBar, { TickBox } from '../ui/bulk-bar';
import useSelection from '../../lib/useSelection';
import { removeMany, confirmCount } from '../../lib/bulkRows';
import CheckScanner from './CheckScanner';
import { formatDate } from '../../lib/dates';

const fmt = (n) => '$' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// A payment date is a calendar date, not an instant: parsed as UTC it
// displayed the day before the money actually came in.
const fmtDate = (d) => formatDate(d, { year: 'numeric', month: 'numeric', day: 'numeric' }) || d || '—';

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const load = React.useCallback(() => {
    Promise.all([apiPayments.list(), apiInvoices.list(), apiCustomers.list()])
      .then(([p, inv, cust]) => {
        setPayments(Array.isArray(p) ? p : []);
        setInvoices(Array.isArray(inv) ? inv : []);
        setCustomers(Array.isArray(cust) ? cust : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const custMap = Object.fromEntries(customers.map(c => [c.id, c]));
  const invMap = Object.fromEntries(invoices.map(i => [i.id, i]));
  const invByNo = Object.fromEntries(invoices.map(i => [String(i.number || ''), i]));
  // Imported payments carry the customer on the payment itself and link to the
  // invoice by number, not by id. Reading only the invoice-by-id route showed
  // "—" for every one of them even though the name was on the record.
  const invFor = (p) => invMap[p.invoiceId || p.invoice_id] || invByNo[String(p.invoiceNumber || '')];
  const addressOf = (p, inv, cust) => [
    p.customerAddress || cust?.address || inv?.customerAddress,
    [p.customerCity || cust?.city, p.customerState || cust?.state, p.customerZip || cust?.zip]
      .filter(Boolean).join(', '),
  ].filter(Boolean).join(' · ');
  const rows = [...payments]
    .map(p => {
      const inv = invFor(p);
      const cust = custMap[p.customerId] || custMap[inv?.customerId];
      return {
        ...p,
        customer: p.customerName || p.customer || cust?.name || inv?.customerName || '—',
        address: addressOf(p, inv, cust) || '—',
        invNo: inv?.number || p.invoiceNumber || p.invoiceId || p.invoice_id || '—',
      };
    })
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const total = payments.reduce((a, p) => a + (Number(p.amount) || 0), 0);
  const now = new Date();
  const thisMonth = payments
    .filter(p => { const d = new Date(p.date); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); })
    .reduce((a, p) => a + (Number(p.amount) || 0), 0);

  // Ticking rows. A bad import is the reason this exists: a payments file that
  // came in against the wrong customers had to be unpicked one row at a time,
  // and there was no row menu to do it with.
  const sel = useSelection(rows.map((p) => p.id).filter(Boolean));
  const picked = () => rows.filter((p) => p.id && sel.has(p.id));

  const bulkDelete = async () => {
    const ids = sel.chosen;
    if (!ids.length) return;
    const sum = picked().reduce((a, p) => a + (Number(p.amount) || 0), 0);
    if (!confirmCount('Delete', ids.length, 'payment',
                      `That is ${fmt(sum)} of recorded money, and it cannot be undone. `
                      + 'The invoices they were applied to are not changed.')) return;
    await removeMany(apiPayments, ids, 'payment');
    sel.clear();
    load();
  };

  const exportPicked = () => {
    const chosen = picked();
    if (!chosen.length) return;
    downloadFile('xpertpests-payments-selected.csv', toCSV(chosen, [
      { key: 'date', header: 'Date' }, { key: 'invNo', header: 'Invoice' },
      { key: 'customer', header: 'Customer' }, { key: 'address', header: 'Address' },
      { key: 'method', header: 'Method' }, { key: 'checkNumber', header: 'Check #' },
      { key: 'amount', header: 'Amount' }]));
    toast.success(`Exported ${chosen.length} payment(s)`);
  };

  const onExport = () => {
    const csv = toCSV(rows, [
      { key: 'date', header: 'Date' }, { key: 'invNo', header: 'Invoice' },
      { key: 'customer', header: 'Customer' }, { key: 'address', header: 'Address' },
      { key: 'method', header: 'Method' }, { key: 'checkNumber', header: 'Check #' },
      { key: 'amount', header: 'Amount' },
    ]);
    downloadFile('xpertpests-payments.csv', csv);
    toast.success('Exported payments');
  };

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Payments</h1>
          <p className="text-slate-500 mt-1">Money collected — card, cash, check, and online.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onExport} className="gap-1.5"><Download className="w-4 h-4" />Export CSV</Button>
          <Button onClick={() => setScanning(true)} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="scan-check-btn"><Camera className="w-4 h-4" />Scan a check</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="p-5 border-slate-200 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><DollarSign className="w-5 h-5" /></div>
          <div><div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Collected</div><div className="text-2xl font-extrabold text-slate-900 tabular-nums">{fmt(total)}</div></div>
        </Card>
        <Card className="p-5 border-slate-200 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><TrendingUp className="w-5 h-5" /></div>
          <div><div className="text-xs font-bold text-slate-500 uppercase tracking-wider">This Month</div><div className="text-2xl font-extrabold text-slate-900 tabular-nums">{fmt(thisMonth)}</div></div>
        </Card>
        <Card className="p-5 border-slate-200 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center"><CreditCard className="w-5 h-5" /></div>
          <div><div className="text-xs font-bold text-slate-500 uppercase tracking-wider"># Payments</div><div className="text-2xl font-extrabold text-slate-900 tabular-nums">{payments.length}</div></div>
        </Card>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <BulkBar count={sel.count} onClear={sel.clear} testId="payments-bulk-bar"
                 countTestId="payments-bulk-count">
          <Button size="sm" variant="outline" className="h-8 gap-1 bg-white" onClick={exportPicked} data-testid="payments-bulk-export-btn">
            <Download className="w-3.5 h-3.5" />Export
          </Button>
          <Button size="sm" variant="outline" className="h-8 gap-1 bg-white text-red-600 hover:text-red-700" onClick={bulkDelete} data-testid="payments-bulk-delete-btn">
            <Trash2 className="w-3.5 h-3.5" />Delete
          </Button>
        </BulkBar>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3 text-left w-10">
                  <TickBox checked={sel.allPicked} onChange={sel.toggleAll}
                           label="Select every payment listed" testId="payments-select-all"/>
                </th>
                <th className="px-5 py-3 text-left">Date</th>
                <th className="px-5 py-3 text-left">Invoice</th>
                <th className="px-5 py-3 text-left">Customer</th>
                <th className="px-5 py-3 text-left">Address</th>
                <th className="px-5 py-3 text-left">Method</th>
                <th className="px-5 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500">No payments recorded yet. Payments appear here when an invoice is paid (online or marked paid).</td></tr>
              )}
              {rows.map((p, i) => (
                <tr key={p.id || i} className={`hover:bg-slate-50 ${p.id && sel.has(p.id) ? 'bg-[#8BC53F]/5' : ''}`}>
                  <td className="px-5 py-3">
                    {/* A payment with no id of its own cannot be acted on, so it
                        gets no tick box rather than one that quietly does nothing. */}
                    {p.id ? (
                      <TickBox checked={sel.has(p.id)} onChange={() => sel.toggle(p.id)}
                               label={`Select payment ${p.id}`} testId={`payments-select-${p.id}`}/>
                    ) : null}
                  </td>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{fmtDate(p.date)}</td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-600">{p.invNo}</td>
                  <td className="px-5 py-3 font-semibold text-slate-900">{p.customer}</td>
                  <td className="px-5 py-3 text-slate-600">{p.address}</td>
                  <td className="px-5 py-3 text-slate-600">{p.method || '—'}</td>
                  <td className="px-5 py-3 text-right font-bold tabular-nums text-emerald-600">{fmt(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {scanning && <CheckScanner onClose={() => setScanning(false)} onRecorded={load} />}
    </div>
  );
}
